import { useState, useEffect } from 'react';
import { useRemoteConfig } from './use-remote-config';

// List of feature flags we can turn On/Off
// When removing a flag from this list. Make sure to also remove the flags from the Firebase console.
// Those flags are controlled by Firebase remote config on `Prod` and `Non-Prod` environments.
const FEATURE_FLAGS_LOCALLY = {
  enable_marketing_consent: true,
  enable_first_class_citizen: true,
  get_blinq_api_redirect: true,
  google_workspace_integration: true,
  html_email_signature: true,
  session_continuity_qr_code: true,
  paginate_team_cards: true,
  workspace_sso_config: true,
  show_billing_and_subscription_settings_page: true,
  templates_v3_in_menu: true,
  enable_home_page: true,
  enable_home_page_onboarding: false,
  home_page_onboarding_item_2_complete: false,
  home_page_onboarding_item_3_complete: false,
  business_onboarding_v2: false,
};

type FeatureFlagKey = keyof typeof FEATURE_FLAGS_LOCALLY;
const featureFlagPrefix = 'flags__';

/** Get the value of a feature flag given a specific key  */
export const useFeatureFlag = (key: FeatureFlagKey): boolean => {
  const [value, setValue] = useState(false);
  const remoteConfig = useRemoteConfig();

  useEffect(() => {
    if (window?.location?.hostname === 'localhost') {
      setValue(FEATURE_FLAGS_LOCALLY[key]);
      return;
    }

    // The value of the feature flag can be overridden by setting a value in local storage.
    const localStorageOverrideValue = window.localStorage.getItem(featureFlagPrefix + key);
    if (localStorageOverrideValue === 'true') {
      setValue(true);
      return;
    }

    if (remoteConfig) {
      setValue(remoteConfig.getBoolean(featureFlagPrefix + key));
    }
  }, [key, remoteConfig]);

  return value;
};
